import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import StatusBadge from "../../Common/UI/StatusTag";

type RoleInfo = {
  id: string;
  label: string;
  code: string;
};

type ProfileData = {
  userId: string;
  inviteStatus: string;
  accountStatus: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  roles: RoleInfo[];
};

// Derives the display status badge from the two backend status fields.
const getStatusBadge = (
  inviteStatus: string,
  accountStatus: string
): { label: string; variant: "active" | "pending" | "cancelled" } => {
  if (inviteStatus === "invited") {
    return { label: "Invited", variant: "pending" };
  }
  if (accountStatus === "inactive") {
    return { label: "Inactive", variant: "cancelled" };
  }
  return { label: "Active", variant: "active" };
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const UserProfile = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileData | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(`/platform/users/${userId}`);
        const data = res.data?.data;
        setProfile({
          userId: data?.userId || "",
          inviteStatus: data?.status || "",
          accountStatus: data?.user?.status || "",
          firstName: data?.user?.firstName || "-",
          lastName: data?.user?.lastName || "-",
          email: data?.user?.email || "-",
          createdAt: data?.createdAt || "",
          roles:
            data?.platformRoles
              ?.map((pr: any) => pr?.role)
              ?.filter((r: any) => r) || [],
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }} color="text.secondary">
          User profile could not be loaded.
        </Typography>
      </Box>
    );
  }

  const badge = getStatusBadge(profile.inviteStatus, profile.accountStatus);
  const createdLabel = profile.createdAt
    ? DateTime.fromISO(profile.createdAt).toFormat("dd MMM yyyy, hh:mm a")
    : "-";

  return (
    <Box sx={{ p: 2.5 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Users
      </Button>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "10px",
          p: 3,
          maxWidth: 720,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h3" color="text.primary">
            {`${profile.firstName} ${profile.lastName}`.trim() || "-"}
          </Typography>
          <StatusBadge label={badge.label} variant={badge.variant} />
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Field label="Email Address">
          <Typography variant="body1">{profile.email}</Typography>
        </Field>

        <Field label="Role(s)">
          {profile.roles.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {profile.roles.map((role) => (
                <Chip key={role.id} label={role.label} size="small" />
              ))}
            </Box>
          ) : (
            <Typography variant="body1">-</Typography>
          )}
        </Field>

        <Field label="Date of Creation">
          <Typography variant="body1">{createdLabel}</Typography>
        </Field>
      </Box>
    </Box>
  );
};

export default UserProfile;
